import type { Permissions, PermissionSource } from '@repo/types/globals';
import type { ComponentProps } from 'react';

import { Label } from '@radix-ui/react-label';
import { ChevronDown, UsersIcon, UserX } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { PermissionDetail } from '@/hooks/fs';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerBody, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ListItem, ListItemContent, ListItemDescription, ListItemTitle } from '@/components/ui/list';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useFSEntryPermissions } from '@/hooks/fs';
import { useMediaQuery } from '@/hooks/media-query';
import { getAvatar } from '@/utils/getAvatar/getAvatar';

interface EntityData {
  id: number;
  title: string;
  detail?: string;
  avatarURL?: string;
}

interface PermissionLiEntityProps {
  data: EntityData;
  sources?: PermissionSource[];
  permissions: Permissions;
  onRemove: () => void;
  onPermissionChange: (id: number, newValue: string) => void;
}

export default function PermissionLiEntity(permissionLiEntityProps: PermissionLiEntityProps) {
  const { data, permissions, sources, onRemove, onPermissionChange } = permissionLiEntityProps;
  const isDesktop = useMediaQuery('sm');
  const { currentPermission, permissionsArray } = useFSEntryPermissions({
    permissions,
  });

  const [access, setAccess] = useState<string>(currentPermission.type);
  const isInherited = useMemo<boolean>(() => sources?.some((source) => source.isInherited) || false, [sources]);

  const handleAccessChange = useCallback(
    (value: string) => {
      onPermissionChange(data.id, value);
      setAccess(value);
    },
    [data.id, onPermissionChange]
  );

  // Return different views based on inheritance and device type
  if (isInherited) {
    return <PermissionLiEntityItem data={data} permissions={currentPermission} sources={sources} />;
  }

  if (isDesktop) {
    return (
      <DropdownMenu>
        <PermissionLiEntityItem data={data} permissions={currentPermission} sources={sources} />
        <DropdownMenuContent align={'end'} className="w-full max-w-88">
          <DropdownMenuRadioGroup value={access} onValueChange={handleAccessChange}>
            {permissionsArray.map((permission) => (
              <DropdownMenuRadioItem key={permission.type} value={permission.type} className="h-fit">
                <div className="space-y-0.5">
                  <p>{permission.title}</p>
                  <p className="text-text-tertiary ms-1 text-xs">{permission.description}</p>
                </div>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem variant="destructive" onClick={onRemove}>
              <UserX />
              Usuń dostęp
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <PermissionLiEntityItem data={data} permissions={currentPermission} sources={sources} />
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Poświadczenia</DrawerTitle>
        </DrawerHeader>
        <DrawerBody className={'flex flex-col gap-4'}>
          <div className="space-y-4 p-3">
            <RadioGroup value={access} onValueChange={handleAccessChange}>
              {permissionsArray.map((permission) => (
                <div
                  key={permission.type}
                  className="has-data-[state=checked]:border-border-brand has-data-[state=checked]:bg-surface-primary-hover relative flex w-full items-start gap-2 rounded-md border p-3 shadow-xs outline-none"
                >
                  <RadioGroupItem value={permission.type} id={permission.type} aria-describedby={`${permission.type}-description`} className="order-1 after:absolute after:inset-0" />
                  <div className="grid grow gap-2">
                    <Label htmlFor={permission.type}>{permission.title}</Label>
                    <p id={`${permission.type}-description`} className="text-text-tertiary text-xs">
                      {permission.description}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>

            <Button onClick={onRemove} className={'w-full'} variant={'outline'} variantColor={'destructive'}>
              <UserX />
              Usuń dostęp
            </Button>
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export interface PermissionLiEntityItemProps extends ComponentProps<typeof ListItem> {
  data: EntityData;
  permissions: PermissionDetail;
  sources?: PermissionSource[];
}

export function PermissionLiEntityItem(permissionLiEntityItemProps: PermissionLiEntityItemProps) {
  const { data, sources, permissions, ...props } = permissionLiEntityItemProps;
  const isDesktop = useMediaQuery('sm');

  const isInherited = useMemo<boolean>(() => sources?.some((source) => source.isInherited) || false, [sources]);

  return (
    <ListItem size={'double'} className="hover:bg-inherit" {...props}>
      <Avatar size={'sm'}>
        <AvatarImage src={getAvatar(data.avatarURL)} alt={'avatar'} />
        <AvatarFallback>
          <UsersIcon className="size-3" />
        </AvatarFallback>
      </Avatar>
      <ListItemContent>
        <ListItemTitle>{data.title}</ListItemTitle>
        <ListItemDescription>{permissions.title}</ListItemDescription>
      </ListItemContent>
      {isInherited ? (
        <Badge variant={'muted'} border size={'md'} className="ml-auto h-9">
          Dziedziczone
        </Badge>
      ) : isDesktop ? (
        <DropdownMenuTrigger asChild>
          <Button className={'ml-auto min-w-14'} variant={'outline'} size={'sm'}>
            {permissions.title}
            <ChevronDown className={'ml-auto size-3'} />
          </Button>
        </DropdownMenuTrigger>
      ) : (
        <ChevronDown className={'stroke-icon-tertiary ml-auto size-4'} />
      )}
    </ListItem>
  );
}
